import type {
  GoldTier,
  VaultStatus,
  AdminFeedPostsResponse,
  FeedCommentResponse,
  FeedCommentsResponse,
  FeedCrownResponse,
  FeedLeaderboardsResponse,
  FeedPageResponse,
  FeedPostResponse,
  AdminClipsResponse,
  AdminCollectionsResponse,
  AdminFapsCodeBatch,
  AdminFapsLedgerRow,
  AdminFapsMintResponse,
  FapClickResponse,
  FapsChestDeal,
  FapsChestResponse,
  FapsKind,
  FapsRedeemResponse,
  QuestId,
  FapsWalletResponse,
  AdminTagCountsResponse,
  AdminTagReportsResponse,
  ReportTagRequest,
  ResolveTagReportRequest,
  TagReportResolution,
  AdminTagsResponse,
  AdminTagSuggestionsResponse,
  AdminWallResponse,
  AdminWallUploadsResponse,
  CatalogBrowseResponse,
  CatalogFacetCountsResponse,
  CatalogFacets,
  CatalogSort,
  CatalogTagsResponse,
  ClipCollectionsResponse,
  ClipMetaResponse,
  ClipTagsResponse,
  SuggestTagRequest,
  AdminLoginResponse,
  AdminGoldGrant,
  AdminLabelAxesResponse,
  AdminUsersResponse,
  AdminLabelImportResponse,
  AdminSettingsResponse,
  ApiError,
  AccountProfilePatch,
  AccountProfileResponse,
  AuthResponse,
  RegisterRequest,
  RecoverStartRequest,
  RecoverStartResponse,
  RecoverFinishRequest,
  ChangePasswordRequest,
  DeleteAccountRequest,
  GoonchaChallenge,
  GoonchaPass,
  CatalogResponse,
  ClassificationResponse,
  ClipDetailsResponse,
  ClipVerdict,
  Device,
  DeviceKind,
  DevicesResponse,
  FapStatusResponse,
  GoonSessionResponse,
  GoonSessionStopResponse,
  GoonSessionHistoryResponse,
  GoonSessionSummaryResponse,
  LandingStats,
  GoonStats,
  MediaItem,
  Playlist,
  PlaylistDetail,
  PlaylistsResponse,
  BulkCollectionRequest,
  BulkCollectionResponse,
  PublicPlaylistDetail,
  PublicPlaylistsResponse,
  PublicProfileResponse,
  SourceConfig,
  SourceFetchRequest,
  SourceFetchResponse,
  WallCheckoutResponse,
  WallDefendResponse,
  WallId,
  WallStateResponse,
  WallUploadsResponse,
  ShopCatalogResponse,
  ShopCheckoutRequest,
  ShopCheckoutResponse,
  ShopFindOrderRequest,
  ShopOrder,
  ShopOrderDetail,
  ShopProduct,
  SystemSettings,
  UpdatePlaylistRequest,
  User,
  View,
  ViewsResponse,
  BattlePickRequest,
  BattleStateResponse,
} from "@gooner/shared";
import { SHOP_ORDER_KEY_HEADER, clipKey, viewIdKind } from "@gooner/shared";
import { track } from "../analytics";
import type { Room, RoomCreateBody, RoomResponse, RoomsListResponse } from "@gooner/shared";
import { viewShapeProps } from "./viewShape";

const BASE = "/api";

/** Error thrown by the API client, carrying the HTTP status and any retry hint. */
export class ApiHttpError extends Error {
  /** The parsed refusal body, when there was one — a typed signal (e.g. `goldUpsell`)
   *  beats string-matching the sentence, which is how copy edits break UIs. */
  public apiError?: ApiError;
  constructor(
    public readonly status: number,
    message: string,
    public readonly retryAfterSec?: number,
  ) {
    super(message);
    this.name = "ApiHttpError";
  }
}

/** The refusal body off a thrown API error, or undefined for anything else. */
export function apiErrorOf(e: unknown): ApiError | undefined {
  return e instanceof ApiHttpError ? e.apiError : undefined;
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, {
    ...init,
    // Only send a JSON content-type when there's a body — Fastify 400s a body-less
    // request (e.g. DELETE) that declares content-type: application/json.
    headers: {
      ...(init?.body != null ? { "content-type": "application/json" } : {}),
      ...(init?.headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    let retryAfterSec: number | undefined;
    let parsed: ApiError | undefined;
    try {
      const text = await res.text();
      if (text) {
        try {
          const body = JSON.parse(text) as ApiError;
          if (body?.error) message = `${res.status}: ${body.error}`;
          retryAfterSec = body?.retryAfterSec;
          parsed = body;
        } catch {
          message = `${res.status}: ${text}`;
        }
      }
    } catch {
      /* ignore */
    }
    if (retryAfterSec === undefined) {
      const h = res.headers.get("retry-after");
      if (h) {
        const n = Number.parseInt(h, 10);
        if (Number.isFinite(n)) retryAfterSec = n;
      }
    }
    const err = new ApiHttpError(res.status, message, retryAfterSec);
    err.apiError = parsed;
    throw err;
  }
  // 204 / empty body guard
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * The screen this tab IS, for `/source/fetch` (2026-09-09). A display carries no bearer,
 * so the device id is how the server learns whose screen is asking — the one fact the
 * vault's `uploads:<uid>` needs, and it counts only when the device belongs to an account
 * AND the view it shows carries that source (see SourceFetchRequest.deviceId). Set by
 * DisplayApp for the life of its device and cleared on unmount, so a later anonymous
 * surface in the same tab inherits nothing. Module-level rather than a prop: RegionPlayer
 * is the caller, and threading an id through it for one source would touch every host.
 */
let sourceDevice: string | undefined;
export function setSourceDevice(deviceId: string | undefined): void {
  sourceDevice = deviceId;
}

export const api = {
  // userHttp, not http: a signed-in browser's bearer also names the vault's owner (the
  // control-side previews and GoonFuel play without a device), and /source/fetch never
  // answers 401, so the token-dropping branch cannot fire here.
  /** Goonette of the Hill: the clip that stayed, the clips it beat, the round. Identity is the
   *  request's (bearer + anon id), so a signed-out pick still counts as one viewer's. */
  battlePick: (body: BattlePickRequest) =>
    http<{ ok: true; recorded: number; streak: number }>("/battles/pick", {
      method: "POST",
      headers: identityHeaders(),
      body: JSON.stringify(body),
    }),
  /** Where a signed-in game stands (a fresh game for anyone else). */
  battleState: () => http<BattleStateResponse>("/battles/state", { headers: identityHeaders() }),
  /** Start over: forget the saved game. */
  battleReset: () => http<{ ok: true }>("/battles/reset", { method: "POST", headers: identityHeaders() }),

  fetchSource: (source: SourceConfig, cursor?: string) =>
    userHttp<SourceFetchResponse>("/source/fetch", {
      method: "POST",
      body: JSON.stringify({
        source,
        cursor,
        ...(sourceDevice ? { deviceId: sourceDevice } : {}),
      } satisfies SourceFetchRequest),
    }),

  // ---- The Feed (2026-09-13) — the public reads. userHttp, not http: a bearer that
  // rides marks `mine` and shows the caller their own pending upload post; the
  // server never 401s these, so the token-dropping branch cannot fire.
  /** GOON ROOMS. userHttp on the reads: a bearer marks `mine`, and the doors never 401. */
  rooms: () => userHttp<RoomsListResponse>("/rooms"),
  room: (id: string) => userHttp<RoomResponse>(`/rooms/${encodeURIComponent(id)}`),

  feed: (before?: number) =>
    userHttp<FeedPageResponse>(`/feed${before ? `?before=${encodeURIComponent(String(before))}` : ""}`),
  feedPost: (id: string) => userHttp<FeedPostResponse>(`/feed/posts/${encodeURIComponent(id)}`),
  feedComments: (id: string) => userHttp<FeedCommentsResponse>(`/feed/posts/${encodeURIComponent(id)}/comments`),
  feedLeaderboards: () => http<FeedLeaderboardsResponse>("/feed/leaderboards"),

  /** A wall's board state — public, holder names + live min bids. Bare is the Community
   *  Wall, the shape the route grew up answering; any other wall names itself. */
  wallState: (wall: WallId = "main") =>
    http<WallStateResponse>(wall === "main" ? "/wall/state" : `/wall/state?wall=${encodeURIComponent(wall)}`),

  /**
   * Age verification. The server owns this decision now: passing mints an
   * httpOnly cookie, and media URLs are only signed for — and only served to —
   * a holder of that cookie. The browser cannot see or forge the cookie, which
   * is the point; the old localStorage flag gated nothing but the UI.
   */
  age: {
    status: () =>
      http<{ required: boolean; minAge: number; verified: boolean; expiresAt: number | null }>(
        "/age/status",
      ),
    /**
     * State that the caller meets the minimum age.
     *
     * Took a date of birth, which looked like verification and was not — nothing checked
     * it against anything. The token this mints is the record that somebody attested, and
     * that token is what actually gates bytes.
     */
    verify: () =>
      http<{ verified: true; minAge: number }>("/age/verify", {
        method: "POST",
        body: JSON.stringify({ confirmed: true }),
      }),
  },

  // ---- goon sessions (per operator; identity is the whole claim) ----
  stats: {
    /*
     * The landing's four vanity figures. No identity headers: it is a site-wide aggregate
     * on a public page, and sending an anon id would only make it look personal.
     *
     * The baselines are already added server-side, so what arrives is the number to show.
     */
    landing: () => http<LandingStats>("/stats/landing"),
  },

  /**
   * The goon session. Every call takes an optional `deviceId`: a SCREEN passes the device
   * it is, and then acts for that device's OWNER rather than for its own browser identity
   * — a television claimed from a phone has an identity of its own that nobody's session
   * runs under, and without this its views, edges and Goon button all landed there. A
   * remote or a browsing page passes nothing and is itself.
   */
  session: {
    get: (deviceId?: string) =>
      http<GoonSessionResponse>(
        `/session${deviceId ? `?deviceId=${encodeURIComponent(deviceId)}` : ""}`,
        { headers: identityHeaders() },
      ),
    /*
     * Session analytics hang off the API client, not off the two call sites (DisplayApp
     * and Goontroller), so a third way to start a session cannot be added without being
     * counted. Tracked on SUCCESS only — a start that failed is not a session, and
     * counting the attempt would inflate the funnel with sessions nobody ever saw.
     *
     * No clip data here or anywhere near it: what was watched during the session stays in
     * session_clip_views. Only that one happened, and how long it lasted.
     */
    start: (deviceId?: string) =>
      http<GoonSessionResponse>("/session/start", {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify({ deviceId }),
      }).then((r) => {
        track("session_started");
        return r;
      }),
    edge: (deviceId?: string) =>
      http<GoonSessionResponse>("/session/edge", {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify({ deviceId }),
      }),
    stop: (deviceId?: string) =>
      http<GoonSessionStopResponse>("/session/stop", {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify({ deviceId }),
      }).then((r) => {
        const s = r.session;
        // Aggregates of the session, not its contents: how long, how many edges, how many
        // clips went by, roughly how much media. No Faps props any more (2026-09-09):
        // watch-time earning no longer ties to a session's lifecycle at all, so a stop
        // has nothing of its own to report there — see FapsService.settleWatchThreshold.
        // The favourite clip in the same response is exactly what does NOT go — see the
        // header of analytics.ts.
        track("session_ended", {
          duration_sec:
            s?.startedAt != null
              ? Math.round(((s.endedAt ?? Date.now()) - s.startedAt) / 1000)
              : null,
          edges: s?.edges ?? null,
          views: s?.views ?? null,
          bytes_mb: s?.bytes != null ? Math.round(s.bytes / 1_000_000) : null,
        });
        return r;
      }),
    view: (clip: MediaItem, deviceId?: string) =>
      http<GoonSessionResponse>("/session/view", {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify({ clip, deviceId }),
      }),
    /** What this screen was showing when an edge landed (DisplayApp's edge effect). */
    creditEdge: (at: number, clips: MediaItem[], deviceId?: string) =>
      http<{ ok: true }>("/session/edge-credit", {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify({ at, clips, deviceId }),
      }),
    /** Seconds watched per clip while a session ran. WHEN is lib/watchReport.ts's rule. */
    watch: (events: Array<{ clip: MediaItem; seconds: number }>, deviceId?: string, keepalive = false) =>
      http<{ ok: true }>("/session/watch", {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify({ events, deviceId }),
        keepalive,
      }),
    /** The rich recap for a finished session — the aggregates the summary card draws.
     *  Its own read rather than part of `stop`, so the hot path stays cheap and the card
     *  can be re-opened (or opened on a different surface) from the session id alone. */
    summary: (id: string, deviceId?: string) =>
      http<GoonSessionSummaryResponse>(
        `/session/summary?id=${encodeURIComponent(id)}${deviceId ? `&deviceId=${encodeURIComponent(deviceId)}` : ""}`,
        { headers: identityHeaders() },
      ),
  },

  /** The lifetime per-clip view count. Public — no identity, no session. WHEN to call it
   *  (one per playthrough, batched) is lib/clipViews.ts's rule; nothing should call this
   *  directly. */
  clipViews: {
    /** Report a window's worth of playthroughs. `n` is the count for that clip, so a clip
     *  that looped twice inside the window arrives as one entry saying 2 — the server
     *  clamps it, since this route asks nobody who they are. `keepalive` is for the flush
     *  on the way out, where a normal fetch would be cancelled with the page.
     *
     *  No response is read: the tally comes back from GET /clip-views when something
     *  actually wants to display it. */
    recordMany: (clips: Array<{ clip: MediaItem; n: number }>, keepalive = false) =>
      http<{ ok: true }>("/clip-views", {
        method: "POST",
        body: JSON.stringify({ clips }),
        keepalive,
      }),
  },

  /** Public, read-only system settings (age gate + goon fuel honor these). */
  getSettings: () => http<SystemSettings>("/settings"),

  /** Public discovery: published playlists + Editor's Choice collections. */
  listCollections: () => http<PublicPlaylistsResponse>("/collections"),

  getCollection: (id: string) =>
    http<PublicPlaylistDetail>(`/collections/${encodeURIComponent(id)}`),

  /** The profile behind /u/:username — the share-card QR's destination.
   *
   *  Public, but sent through userHttp so the bearer (if any) rides along: the route
   *  never 401s, and the token is the only way the server can tell that the caller is
   *  the owner looking at their OWN page, which it shows them even while it is private.
   *  Same shape as getClipDetails, which is public but token-aware for the same reason. */
  publicProfile: (username: string) =>
    userHttp<PublicProfileResponse>(`/u/${encodeURIComponent(username)}`),

  /** Browse the shared catalog (visibility-safe: private + banned excluded).
   *  Pass `tag` to filter by an exact keyword; `facets` narrows further and composes —
   *  every selection is ANDed, so {race:"latina", quality:"1080p"} means both.
   *  (No free-text `q`: clips carry no titles, so it had nothing honest to match.) */
  searchCatalog: (
    opts: {
      tag?: string;
      facets?: CatalogFacets;
      limit?: number;
      offset?: number;
      sort?: CatalogSort;
    } = {},
  ) => {
    const p = new URLSearchParams();
    if (opts.tag) p.set("tag", opts.tag);
    for (const [facet, value] of Object.entries(opts.facets ?? {})) {
      if (value) p.set(facet, value);
    }
    if (opts.limit != null) p.set("limit", String(opts.limit));
    if (opts.offset != null) p.set("offset", String(opts.offset));
    if (opts.sort && opts.sort !== "latest") p.set("sort", opts.sort);
    const qs = p.toString();
    return http<CatalogBrowseResponse>(`/catalog${qs ? `?${qs}` : ""}`);
  },

  /** Per-value clip counts for every facet under the given filter — the numbers
   *  beside the browse sidebar rows. Each facet is counted with its own selection
   *  lifted, so the numbers say what switching to a value would give. */
  catalogFacetCounts: (opts: { tag?: string; facets?: CatalogFacets } = {}) => {
    const p = new URLSearchParams();
    if (opts.tag) p.set("tag", opts.tag);
    for (const [facet, value] of Object.entries(opts.facets ?? {})) {
      if (value) p.set(facet, value);
    }
    const qs = p.toString();
    return http<CatalogFacetCountsResponse>(`/catalog/facets${qs ? `?${qs}` : ""}`);
  },

  /** Public tag autocomplete: approved catalog tags with usage counts.
   *  `limit` is clamped to 500 server-side and defaults to 20. */
  catalogTags: (q?: string, limit?: number) => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (limit) p.set("limit", String(limit));
    const qs = p.toString();
    return http<CatalogTagsResponse>(`/catalog/tags${qs ? `?${qs}` : ""}`);
  },

  /**
   * GOONCHA: the robot check gating register/login. `identityHeaders` rides along so
   * the anon id can dedupe repeat answers server-side; a solve's reward is a one-shot
   * pass token the auth calls then carry.
   */
  gooncha: {
    fresh: () =>
      http<GoonchaChallenge | GoonchaPass>("/gooncha/new", {
        method: "POST",
        headers: identityHeaders(),
        body: "{}",
      }),
    solve: (challengeId: string, picks: number[]) =>
      http<GoonchaPass>("/gooncha/solve", {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify({ challengeId, picks }),
      }),
  },

  /** Views: the named screen configurations a device can be pointed at. */
  views: {
    list: () => http<ViewsResponse>("/views", { headers: identityHeaders() }),
    get: (id: string) => http<{ view: View }>(`/views/${encodeURIComponent(id)}`),
    /**
     * "Still open." An unsaved view expires from its last touch, so an editor sitting
     * idle has to say it is still there — see startViewKeepAlive. Nothing is read and
     * nothing is written, which is why it is safe to send on a timer.
     */
    touch: (id: string) =>
      http<{ ok: true }>(`/views/${encodeURIComponent(id)}/touch`, { method: "POST" }),
    /** Mint an unsaved view — it gets an id a screen can be pointed at, but no row.
     *  Identity rides along (optionally, server-side) so the region ceiling is the
     *  caller's own tier rather than the anonymous floor. */
    createTemp: (input: ViewInput) =>
      http<{ view: View }>("/views/temp", {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify(input),
      }),
    /** Fork any resolvable view (builtin, temp, saved) into a fresh temp copy. The
     *  server re-resolves the regions itself — which is what lets a 16-region quick
     *  setup fork past the region caps without trusting anything in a request body. */
    forkFrom: (id: string) =>
      http<{ view: View }>(`/views/temp/from/${encodeURIComponent(id)}`, { method: "POST" }),
    /** Save: a fresh view from `input`, or promote an unsaved one with `{ from }`. */
    save: (body: ViewInput | { from: string }) =>
      http<{ view: View }>("/views", {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify(body),
      }).then((r) => {
        // Saving is the one action that REQUIRES an account (an anon owner gets temp
        // views only), so this doubles as the account-conversion signal. `promoted`
        // separates "saved an existing unsaved view" from "saved a fresh one". The
        // shape props (layout, sources, filters, sync — see viewShapeProps) ride on
        // the SAVE because a save is the one moment a build is declared finished.
        const promoted = typeof (body as { from?: string }).from === "string";
        track("view_saved", { promoted, ...viewShapeProps(r.view?.regions ?? []) });
        return r;
      }),
    // `origin` is required so a new caller has to say whether it is the editor's
    // debounced autosave or a live edit from the wall — the wrapper cannot tell.
    update: (id: string, input: ViewInput, origin: "autosave" | "wall") => {
      const kind = viewIdKind(id) === "temp" ? "temp" : "saved";
      return http<{ view: View }>(`/views/${encodeURIComponent(id)}`, {
        method: "PUT",
        headers: identityHeaders(),
        body: JSON.stringify(input),
      }).then((r) => {
        // Success only: a wall PUT against an expired temp view fails and is
        // swallowed by the caller, and a failed persist edited nothing.
        const shape = input.regions ? viewShapeProps(input.regions) : { regions: null };
        track("view_edited", { kind, trigger: origin, ...shape });
        return r;
      });
    },
    remove: (id: string) => {
      const kind = viewIdKind(id) === "temp" ? "temp" : "saved";
      return http<{ ok: true }>(`/views/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: identityHeaders(),
      }).then((r) => {
        track("view_deleted", { kind });
        return r;
      });
    },
  },

  /**
   * Handing control to another device — "I'm at my desk, I want my phone".
   *
   * The opposite direction to pairing a screen, and for a reason: pairing attaches an
   * account-less TV to an account, so the TV displays; a handoff moves the identity, so
   * the device that HAS it displays and the one acquiring it reads.
   */
  control: {
    /** Mint a token for this browser's identity, to show as a QR. */
    handoff: () =>
      http<{ token: string; expiresAt: number }>("/control/handoff", {
        method: "POST",
        headers: identityHeaders(),
      }).then((r) => {
        // Two halves on two devices, like pairing: this browser shows the QR, another
        // redeems it. The token is a credential and is never sent.
        track("control_handoff_shown");
        return r;
      }),
    /** Redeem one. A session for an account, or the anon id for a signed-out browser. */
    adopt: (token: string) =>
      http<{ token?: string; user?: User; anonId?: string }>("/control/adopt", {
        method: "POST",
        body: JSON.stringify({ token }),
      }).then((r) => {
        track("control_adopted", { account: Boolean(r.user) });
        return r;
      }),
  },

  /**
   * Pairing, in the direction every TV platform uses: the SCREEN shows a code and the
   * CONTROLLER reads it. The screen has no account to attach anything to; the controller
   * does. It also means nobody types on a TV remote, which the old direction required
   * once per screen.
   */
  screens: {
    /** A screen announces itself and gets a code to display. No identity — it has none.
     *  A CLAIMED screen passes its device id, and the code then ADOPTS that device on
     *  redemption (ownership moves in place) instead of minting a new one.
     *
     *  `viewId` is what this screen is PLAYING, so redeeming the code inherits it rather
     *  than replacing it — connecting to a screen must not change what is on it. */
    pending: (kind?: DeviceKind, deviceId?: string, viewId?: string, holdMs?: number) =>
      http<{
        screenId: string;
        code: string;
        expiresAt: number;
        /** A hold already running on this screen — so a tab that has just navigated or
         *  reloaded is not blind to it until its first poll. */
        heldForMs: number | null;
      }>("/screens/pending", {
        method: "POST",
        body: JSON.stringify({ kind, deviceId, viewId, holdMs }),
      }),
    // NOT tracked here, unlike its siblings. `screen_code_shown` lives in usePendingScreen,
    // the one caller, because it alone can tell a first mint from a re-mint after a
    // lapse — and counting both as the same event is how one television sent 618 of them.

    /** What a claimable screen polls: its device id, once somebody has claimed it. Carries
     *  what the screen is playing NOW, so a view swapped mid-wait is the one a claim
     *  inherits. */
    poll: (screenId: string, viewId?: string) =>
      http<{
        code: string;
        expiresAt: number;
        deviceId: string | null;
        /** How much longer this screen is spoken for, or null when free. A DURATION, so a
         *  television with a wrong clock still counts the right number down. */
        heldForMs: number | null;
      }>(
        `/screens/pending/${encodeURIComponent(screenId)}${
          viewId ? `?viewId=${encodeURIComponent(viewId)}` : ""
        }`,
      ),
    /** A controller redeems a screen's code. The device is created here, owned by them. */
    claim: (code: string, name?: string) =>
      http<{ device: Device }>("/screens/claim", {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify({ code, name }),
      }).then((r) => {
        /*
         * THE activation moment for a multi-device app: a second screen now exists and is
         * under someone's control. screen_code_shown -> screen_claimed is the conversion
         * rate that decides whether this product works at all, and it is the one number
         * a generic analytics tool could never have found on its own.
         *
         * Neither the code nor the device id is sent — only the kind of thing paired.
         */
        track("screen_claimed", { kind: r.device?.kind ?? null });
        return r;
      }),
    /** A screen setting its own claim hold, or letting go of one it is running. Gated on
     *  holding the device id, which is what the tab in front of the television has. */
    hold: (deviceId: string, body: { holdMs?: number; release?: boolean }) =>
      http<{ heldForMs: number | null }>(
        `/screens/${encodeURIComponent(deviceId)}/hold`,
        { method: "POST", body: JSON.stringify(body) },
      ),
  },

  /**
   * Devices: the screens under this operator's control.
   *
   * A device is a screen that is connected right now and nothing else — closing one deletes
   * it, and a screen that comes back is a new device. Both creation paths below produce the
   * same throwaway thing; they differ only in what they prove.
   *
   * `get` deliberately sends no identity — it is how a screen reads its own assignment, and
   * a screen has no identity beyond the id in its own URL. Everything that CHANGES a device
   * is owner-checked server-side, so identityHeaders() go on those.
   */
  devices: {
    list: () => http<DevicesResponse>("/devices", { headers: identityHeaders() }),
    get: (id: string) => http<{ device: Device }>(`/devices/${encodeURIComponent(id)}`),
    /** The controller opening a screen on this same machine. No code: it already knows who
     *  you are, so the identity is the claim. */
    open: (body: { name?: string; kind?: DeviceKind; viewId?: string } = {}) =>
      http<{ device: Device }>("/devices/open", {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify(body),
      }).then((r) => {
        /*
         * The other way a screen comes into being: opened directly on this machine, with
         * no code to redeem, because the operator is already identified. Together with
         * screen_claimed this counts every wall that actually starts — and keeping them
         * as separate events is the point, since "paired a TV across the room" and
         * "opened a tab here" are different products being used.
         *
         * No device id and no view id: both identify a specific screen or wall.
         */
        track("wall_started", {
          kind: r.device?.kind ?? body.kind ?? null,
          preassigned_view: typeof body.viewId === "string",
        });
        return r;
      }),
    setShowing: (id: string, viewId: string | null) =>
      http<{ device: Device }>(`/devices/${encodeURIComponent(id)}/showing`, {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify({ viewId }),
      }).then((r) => {
        // Pointing a screen somewhere else is the multi-screen product being USED, as
        // opposed to set up; nothing else counts it. No device id, no view id.
        track("screen_retargeted", { cleared: viewId === null });
        return r;
      }),
    rename: (id: string, name: string) =>
      http<{ device: Device }>(`/devices/${encodeURIComponent(id)}/name`, {
        method: "POST",
        headers: identityHeaders(),
        body: JSON.stringify({ name }),
      }),
    remove: (id: string) =>
      http<{ ok: true }>(`/devices/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: identityHeaders(),
      }),
  },
};

/** The editable half of a View — everything but the id and server-assigned fields. */
export type ViewInput = Pick<View, "name" | "icon" | "aspect" | "adaptiveLayout" | "regions">;

/** What /patreon/link answers: the feature's presence, the door, and the link's word. */
export interface PatreonLinkState {
  enabled: boolean;
  /** The Patreon checkout page (settings.gold.paymentUrl); null until an admin sets it. */
  checkoutUrl: string | null;
  /** The tier's monthly price in dollars (settings.gold.price30d); null when unset. */
  monthlyPrice: number | null;
  linked: {
    name: string | null;
    /** active | declined | former | none | disconnected — see server/src/patreon.ts. */
    status: string;
    nextChargeAt: number | null;
    checkedAt: number;
  } | null;
}

// ---------------------------------------------------------------------------
// Admin API — single-password session held as a bearer token in localStorage.
// ---------------------------------------------------------------------------

const ADMIN_TOKEN_KEY = "gp:admin-token";

export function getAdminToken(): string | null {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY);
  } catch {
    return null;
  }
}
function setAdminToken(token: string): void {
  try {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
  } catch {
    /* storage unavailable — session just won't persist across reloads */
  }
}
export function clearAdminToken(): void {
  try {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** Like http(), but attaches the admin bearer token and drops it on a 401. */
async function adminHttp<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAdminToken();
  const res = await fetch(BASE + path, {
    ...init,
    headers: {
      ...(init?.body != null ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    if (res.status === 401) clearAdminToken();
    let message = `${res.status} ${res.statusText}`;
    try {
      const text = await res.text();
      if (text) {
        try {
          const body = JSON.parse(text) as ApiError;
          if (body?.error) message = `${res.status}: ${body.error}`;
        } catch {
          message = `${res.status}: ${text}`;
        }
      }
    } catch {
      /* ignore */
    }
    throw new ApiHttpError(res.status, message);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const adminApi = {
  login: (password: string) =>
    adminHttp<AdminLoginResponse>("/admin/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    }).then((r) => {
      setAdminToken(r.token);
      return r;
    }),

  logout: () =>
    adminHttp<{ ok: true }>("/admin/logout", { method: "POST" }).finally(clearAdminToken),

  /** Validate the stored session; rejects (and clears the token) if invalid. */
  session: () => adminHttp<{ ok: true }>("/admin/session"),

  getSettings: () => adminHttp<AdminSettingsResponse>("/admin/settings"),

  putSettings: (patch: Partial<SystemSettings>) =>
    adminHttp<AdminSettingsResponse>("/admin/settings", {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  // ---- Users + GoonLords Gold ----
  listUsers: (q?: string) =>
    adminHttp<AdminUsersResponse>(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  grantGold: (userId: string, days: number, note?: string) =>
    adminHttp<{ user: User }>(`/admin/users/${encodeURIComponent(userId)}/gold`, {
      method: "POST",
      body: JSON.stringify({ days, ...(note ? { note } : {}) }),
    }),
  revokeGold: (userId: string) =>
    adminHttp<{ user: User }>(`/admin/users/${encodeURIComponent(userId)}/gold`, {
      method: "DELETE",
    }),
  /** The operator's reset: a random temporary password, shown once; every session ends. */
  resetPassword: (userId: string) =>
    adminHttp<{ password: string }>(`/admin/users/${encodeURIComponent(userId)}/reset-password`, { method: "POST" }),
  listGoldGrants: (userId: string) =>
    adminHttp<{ grants: AdminGoldGrant[] }>(`/admin/users/${encodeURIComponent(userId)}/grants`),
  /** The vault against its budget — the operator's one number for "is disk safe". */
  vaultTotals: () => adminHttp<{ usedBytes: number; budgetBytes: number; enabled: boolean }>("/admin/vault"),
  userVault: (userId: string) =>
    adminHttp<{ vault: VaultStatus; items: MediaItem[] }>(`/admin/users/${encodeURIComponent(userId)}/vault`),
  /** Scoped to the named account on the server too — a mistyped id reaches nobody else's. */
  deleteUserVaultItem: (userId: string, clipId: string) =>
    adminHttp<{ ok: true }>(
      `/admin/users/${encodeURIComponent(userId)}/vault/${encodeURIComponent(clipId)}`,
      { method: "DELETE" },
    ),

  /** Labelshop → catalog: every labelshop axis, what it writes, and whether it is live. */
  labelAxes: () => adminHttp<AdminLabelAxesResponse>("/admin/labels/axes"),
  /** Pull approved labelshop labels on the live axes into the catalog now. Dry run by
   *  default — the one admin POST that deletes (a human value replaces a model's). */
  importLabels: (opts: { dryRun?: boolean } = {}) =>
    adminHttp<AdminLabelImportResponse>("/admin/labels/import", {
      method: "POST",
      body: JSON.stringify({ dryRun: opts.dryRun !== false }),
    }),

  listCatalogClips: (q?: string) =>
    adminHttp<AdminClipsResponse>(`/admin/clips${q ? `?q=${encodeURIComponent(q)}` : ""}`),

  /** Which collections each of these clips belongs to (content-first review). */
  clipCollections: (clipIds: string[]) =>
    adminHttp<ClipCollectionsResponse>("/admin/clips/collections", {
      method: "POST",
      body: JSON.stringify({ clipIds }),
    }),

  /** All distinct freeform clip tags, most-used first (for tag autocomplete). */
  listTags: () => adminHttp<AdminTagsResponse>("/admin/tags"),

  /** All tags with clip counts, for the tag manager. */
  listTagCounts: () => adminHttp<AdminTagCountsResponse>("/admin/tag-counts"),
  /** Remove a tag from every clip. Returns the refreshed tag counts. */
  deleteTag: (tag: string) =>
    adminHttp<AdminTagCountsResponse>("/admin/tags/delete", {
      method: "POST",
      body: JSON.stringify({ tag }),
    }),
  /** Fold `from` into `into` across all clips. Returns the refreshed tag counts. */
  mergeTag: (from: string, into: string) =>
    adminHttp<AdminTagCountsResponse>("/admin/tags/merge", {
      method: "POST",
      body: JSON.stringify({ from, into }),
    }),
  /** Hide or reveal a tag on public surfaces. Returns the refreshed tag counts. */
  setTagVisibility: (tag: string, hidden: boolean) =>
    adminHttp<AdminTagCountsResponse>("/admin/tags/visibility", {
      method: "POST",
      body: JSON.stringify({ tag, hidden }),
    }),
  /** Set a keyword's display name, or clear it (empty string) to fall back to the
   *  name derived from the slug. Returns the refreshed tag counts. */
  setTagDisplayName: (tag: string, displayName: string) =>
    adminHttp<AdminTagCountsResponse>("/admin/tags/display-name", {
      method: "POST",
      body: JSON.stringify({ tag, displayName }),
    }),

  /** The mislabelled-keyword review queue (AI keywords users have reported). */
  listTagReports: () => adminHttp<AdminTagReportsResponse>("/admin/tag-reports"),
  /** Resolve a reported keyword: "keep" promotes it to admin-verified (retiring its
   *  AI badge), "remove" strips it off the clip. Returns the refreshed queue. */
  resolveTagReport: (clipId: string, tag: string, resolution: TagReportResolution) =>
    adminHttp<AdminTagReportsResponse>("/admin/tag-reports/resolve", {
      method: "POST",
      body: JSON.stringify({ clipId, tag, resolution } satisfies ResolveTagReportRequest),
    }),

  /** Pending user tag suggestions awaiting review. */
  /** The Community Wall's placement ledger (live / displaced / revoked). */
  wallList: () => adminHttp<AdminWallResponse>("/admin/wall"),
  /** The upload review queue, pending first. */
  wallUploadQueue: () => adminHttp<AdminWallUploadsResponse>("/admin/wall/uploads"),
  // ---- The Feed: the moderator's list, pulls, and the account ban (the unban's mirror).
  feedPosts: () => adminHttp<AdminFeedPostsResponse>("/admin/feed/posts"),
  feedPostDelete: (id: string) =>
    adminHttp<{ ok: true }>(`/admin/feed/posts/${encodeURIComponent(id)}`, { method: "DELETE" }),
  feedCommentDelete: (id: string) =>
    adminHttp<{ ok: true }>(`/admin/feed/comments/${encodeURIComponent(id)}`, { method: "DELETE" }),
  userBan: (userId: string) =>
    adminHttp<{ ok: true }>(`/admin/users/${encodeURIComponent(userId)}/ban`, { method: "POST" }),
  wallUploadApprove: (id: string) =>
    adminHttp<{ ok: true }>(`/admin/wall/uploads/${encodeURIComponent(id)}/approve`, {
      method: "POST",
    }),
  /** Reject: bans the clip, revokes every square carrying it, and ends the account. */
  wallUploadReject: (id: string, reason?: string) =>
    adminHttp<{ ok: true }>(`/admin/wall/uploads/${encodeURIComponent(id)}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  /** Pull a placement — the square reverts to unsold; nothing is refunded. */
  wallRevoke: (id: string) =>
    adminHttp<{ ok: true }>(`/admin/wall/${encodeURIComponent(id)}/revoke`, { method: "POST" }),

  // FAPS: redeem codes and the ledger.
  /** Mint a batch. The reply is the ONLY time the plaintext exists — show it once. */
  fapsMintCodes: (count: number, amount: number, label: string) =>
    adminHttp<AdminFapsMintResponse>("/admin/faps/codes", {
      method: "POST",
      body: JSON.stringify({ count, amount, label }),
    }),
  fapsListBatches: () => adminHttp<{ batches: AdminFapsCodeBatch[] }>("/admin/faps/codes"),
  /** Cancel a batch's unredeemed codes; redeemed ones are receipts and stay. */
  fapsRevokeBatch: (batchId: string) =>
    adminHttp<{ revoked: number }>(`/admin/faps/codes/${encodeURIComponent(batchId)}/revoke`, { method: "POST" }),
  /** Adjust one account. Negative is a clawback and cannot go below zero. */
  fapsGrant: (userId: string, delta: number, note: string) =>
    adminHttp<{ balance: number }>(`/admin/users/${encodeURIComponent(userId)}/faps`, {
      method: "POST",
      body: JSON.stringify({ delta, note }),
    }),
  fapsLedger: (limit = 200, kind?: FapsKind) =>
    adminHttp<{ rows: AdminFapsLedgerRow[] }>(
      `/admin/faps/ledger?limit=${limit}${kind ? `&kind=${encodeURIComponent(kind)}` : ""}`,
    ),
  fapsAudit: () =>
    adminHttp<{ mismatches: Array<{ userId: string; username: string; balance: number; ledgerSum: number }> }>(
      "/admin/faps/audit",
    ),

  listTagSuggestions: () => adminHttp<AdminTagSuggestionsResponse>("/admin/tag-suggestions"),
  acceptTagSuggestion: (id: number) =>
    adminHttp<{ ok: true }>(`/admin/tag-suggestions/${id}/accept`, { method: "POST" }),
  rejectTagSuggestion: (id: number) =>
    adminHttp<{ ok: true }>(`/admin/tag-suggestions/${id}/reject`, { method: "POST" }),

  /** Per-clip metadata: freeform tags + banned state. */
  getClipMeta: (clipId: string) =>
    adminHttp<ClipMetaResponse>(`/admin/clips/meta?clipId=${encodeURIComponent(clipId)}`),

  setClipMeta: (clip: MediaItem, patch: { tags?: string[]; banned?: boolean }) =>
    adminHttp<ClipMetaResponse>("/admin/clips/meta", {
      method: "POST",
      body: JSON.stringify({ clip, ...patch }),
    }),

  /** Position-review dataset tool: the review queue for a pos:<label>, and recording a
   *  human-verified label (which also fixes the live tag). */
  posReviewClips: (label: string) =>
    adminHttp<{ items: MediaItem[] }>(`/admin/pos/clips?label=${encodeURIComponent(label)}`),
  setPosLabel: (clipId: string, label: string) =>
    adminHttp<{ ok: true }>("/admin/pos/label", {
      method: "POST",
      body: JSON.stringify({ clipId, label }),
    }),

  /** Editor's Choice collection curation. */
  collections: {
    list: () => adminHttp<AdminCollectionsResponse>("/admin/collections"),
    create: (name: string, description?: string) =>
      adminHttp<Playlist>("/admin/collections", {
        method: "POST",
        body: JSON.stringify({ name, description }),
      }),
    get: (id: string) => adminHttp<PlaylistDetail>(`/admin/collections/${encodeURIComponent(id)}`),
    update: (id: string, patch: UpdatePlaylistRequest) =>
      adminHttp<Playlist>(`/admin/collections/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    remove: (id: string) =>
      adminHttp<{ ok: true }>(`/admin/collections/${encodeURIComponent(id)}`, { method: "DELETE" }),
    addItem: (id: string, clip: MediaItem) =>
      adminHttp<{ ok: true }>(`/admin/collections/${encodeURIComponent(id)}/items`, {
        method: "POST",
        body: JSON.stringify({ clip }),
      }),
    removeItem: (id: string, clipId: string) =>
      adminHttp<{ ok: true }>(
        `/admin/collections/${encodeURIComponent(id)}/items/${encodeURIComponent(clipId)}`,
        { method: "DELETE" },
      ),
  },
};

// ---------------------------------------------------------------------------
/** `{ is_test_bot: true }` when the server flagged this auth response as QA test-bot
 *  traffic (AuthResponse.testBot), else nothing — ordinary sign-ins are unchanged. */
export function testBotProps(r: { testBot?: true }): { is_test_bot: true } | undefined {
  return r.testBot === true ? { is_test_bot: true } : undefined;
}

// User accounts + clip catalog — optional bearer token in localStorage.
// ---------------------------------------------------------------------------

const USER_TOKEN_KEY = "gp:user-token";

export function getUserToken(): string | null {
  try {
    return localStorage.getItem(USER_TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setUserToken(token: string): void {
  try {
    localStorage.setItem(USER_TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}
export function clearUserToken(): void {
  try {
    localStorage.removeItem(USER_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

// A stable per-browser id so signed-out users still own their caves (server-scoped
// to "anon:<id>"); persists in localStorage and survives until they clear storage.
const ANON_ID_KEY = "gp:anon-id";
/**
 * Adopt an anon id handed over by another device.
 *
 * A signed-out browser's identity IS this string, so a control handoff from one has to
 * move it — otherwise the phone arrives as a stranger with an empty library, which is the
 * failure the handoff exists to prevent.
 */
export function setAnonId(id: string): void {
  try {
    localStorage.setItem(ANON_ID_KEY, id);
  } catch {
    /* storage unavailable — the caller reloads and this browser stays itself */
  }
}

/**
 * A fresh anon id, without requiring a secure context.
 *
 * `crypto.randomUUID()` exists only under HTTPS or localhost. Reached over a LAN or
 * Tailscale address — which is exactly how a phone reaches a dev server — it is
 * `undefined`, so the previous version threw, hit its catch, and returned the literal
 * "anon-local" for EVERY browser that got there. They then all shared one identity: one
 * library, one set of devices, one of everything, presenting as a baffling sync bug rather
 * than an id bug.
 *
 * `crypto.getRandomValues` is not gated on a secure context, so it carries this. Math.random
 * is the last resort — weak, but an id that is merely weak beats an id that is shared.
 */
function freshAnonId(): string {
  const c: Crypto | undefined = typeof crypto !== "undefined" ? crypto : undefined;
  if (c?.randomUUID) return c.randomUUID();
  if (c?.getRandomValues) {
    const b = c.getRandomValues(new Uint8Array(16));
    // RFC 4122 v4 shape, so the id looks like every other one in the system.
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const hex = [...b].map((n) => n.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return `anon-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export function getAnonId(): string {
  try {
    let id = localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      id = freshAnonId();
      localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  } catch {
    // Storage genuinely unavailable (private mode, blocked cookies). Still per-session
    // unique rather than a shared constant — this browser simply forgets on reload.
    return freshAnonId();
  }
}

/** Identity headers for owner-scoped resources (caves): the user bearer token when
 *  signed in, plus the stable anon id so signed-out browsers own their caves too. */
function identityHeaders(): Record<string, string> {
  const token = getUserToken();
  return {
    ...(token ? { authorization: `Bearer ${token}` } : {}),
    "x-anon-id": getAnonId(),
  };
}

/** Like http(), but attaches the user bearer token and drops it on a 401. */
async function userHttp<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getUserToken();
  const res = await fetch(BASE + path, {
    ...init,
    headers: {
      ...(init?.body != null ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    if (res.status === 401) clearUserToken();
    let message = `${res.status} ${res.statusText}`;
    let parsed: ApiError | undefined;
    try {
      const text = await res.text();
      if (text) {
        try {
          const body = JSON.parse(text) as ApiError;
          if (body?.error) message = `${res.status}: ${body.error}`;
          parsed = body;
        } catch {
          message = `${res.status}: ${text}`;
        }
      }
    } catch {
      /* ignore */
    }
    // The collections cap answers through THIS client, so the typed refusal body must
    // survive here too, not only in `http`.
    const err = new ApiHttpError(res.status, message);
    err.apiError = parsed;
    throw err;
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const userApi = {
  /** The profile's session history and its delete — bearer only, see the routes. */
  session: {
    history: () => userHttp<GoonSessionHistoryResponse>("/session/history"),
    remove: (id: string) =>
      userHttp<{ ok: true }>(`/session/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },
  /** The shop. Bearer because a preview account can see it while it is closed to everyone. */
  shopCatalog: () => userHttp<ShopCatalogResponse>("/shop/products"),
  shopProduct: (slug: string) => userHttp<ShopProduct>(`/shop/products/${encodeURIComponent(slug)}`),
  /** A slug, never a price: the server reads that from the catalog. */
  shopCheckout: (body: ShopCheckoutRequest) =>
    userHttp<ShopCheckoutResponse>("/shop/checkout", { method: "POST", body: JSON.stringify(body) }),
  shopOrders: () => userHttp<{ orders: ShopOrder[] }>("/shop/orders"),
  /** `key` is a guest's order key: it opens the order with no account. */
  shopOrder: (id: string, key?: string | null) =>
    userHttp<ShopOrderDetail>(`/shop/orders/${encodeURIComponent(id)}`, {
      headers: key ? { [SHOP_ORDER_KEY_HEADER]: key } : {},
    }),
  shopOrderByTxn: (txn: string, key?: string | null) =>
    userHttp<{ order: ShopOrder | null }>(`/shop/orders/by-txn/${encodeURIComponent(txn)}`, {
      headers: key ? { [SHOP_ORDER_KEY_HEADER]: key } : {},
    }),
  shopFindOrder: (body: ShopFindOrderRequest) =>
    userHttp<ShopOrderDetail>("/shop/orders/find", { method: "POST", body: JSON.stringify(body) }),
  /**
   * Start a Gold checkout: we mint a PayzMoon payment intent server-side and hand back
   * their hosted checkout url.
   *
   * The tier is a name, never an amount — a client that sent the price would be a client
   * that could set it. The email is the buyer's receipt address and is passed to the
   * processor without being stored: accounts here are username-only, and this does not
   * change that.
   */
  goldCheckout: (tier: GoldTier, email: string) =>
    userHttp<{ checkoutUrl: string; transactionId: string }>("/gold/checkout", {
      method: "POST",
      body: JSON.stringify({ tier, email }),
    }),

  /**
   * Premium via Patreon. `patreonLink` says whether the feature exists here, where to
   * subscribe, and what Patreon last said about this account's link — status and dates
   * only, never a token. `patreonConnect` mints the consent url the browser must then
   * NAVIGATE to (the callback lands on /gold/thanks) — `source` is the surface that
   * pressed Connect, carried through the round-trip so the server's patreon_linked can
   * name it; `patreonRefresh` asks Patreon again right now, saying what prompted the
   * ask (the tab's return, the panel's poll, or the button) — the server throttles it
   * per account, so a burst costs nothing; `patreonUnlink` severs the link and leaves
   * Gold to run out.
   */
  patreonLink: () => userHttp<PatreonLinkState>("/patreon/link"),
  patreonConnect: (source: string) =>
    userHttp<{ url: string }>("/patreon/connect", { method: "POST", body: JSON.stringify({ source }) }),
  patreonRefresh: (trigger: "return" | "poll" | "manual" = "manual") =>
    userHttp<PatreonLinkState>("/patreon/refresh", { method: "POST", body: JSON.stringify({ trigger }) }),
  patreonUnlink: () => userHttp<PatreonLinkState>("/patreon/link", { method: "DELETE" }),

  /**
   * Take a Community Wall square: 1–5 clip ids (public tokens — the server resolves
   * and re-vets them), an email for the receipt (gold's rule: processor's business,
   * never stored). Same name-not-amount discipline as goldCheckout — the price is
   * computed server-side from the square and the decay clock; a client that sent one
   * would be a client that could set one. A free claim at the floor comes back as
   * `{ claimed: true }` with no checkoutUrl — the placement is already live and the
   * email was never read.
   */
  wallCheckout: (regionId: string, clips: string[], uploads: string[], email: string) =>
    userHttp<WallCheckoutResponse>("/wall/checkout", {
      method: "POST",
      body: JSON.stringify({ regionId, clips, uploads, email }),
    }),


  /** Defend a square you hold — re-buy it at its current takeover price (Faps only). */
  wallDefend: (regionId: string) =>
    userHttp<WallDefendResponse>("/wall/defend", {
      method: "POST",
      body: JSON.stringify({ regionId }),
    }),

  // ---- The Feed: the owner's doors. `clip` is the pick's wire token (`clipKey(item)`:
  // a catalog token, or `upload:<token>` for the poster's own vault item) — the server
  // resolves and re-vets it, so nothing here is trusted.
  /** `what` is a clip's wire token or `{collection: id}` — one of your PUBLIC collections. */
  feedPost: (what: { clip: string } | { collection: string }, caption: string) =>
    userHttp<FeedPostResponse>("/feed/posts", { method: "POST", body: JSON.stringify({ ...what, caption }) }),
  feedDelete: (id: string) => userHttp<{ ok: true }>(`/feed/posts/${encodeURIComponent(id)}`, { method: "DELETE" }),
  /** The crown, on the post's clip — the same crown as anywhere; spammable like fapClick. */
  feedCrown: (id: string) =>
    userHttp<FeedCrownResponse>(`/feed/posts/${encodeURIComponent(id)}/crown`, { method: "POST" }),
  feedComment: (id: string, body: string, parentId?: string) =>
    userHttp<FeedCommentResponse>(`/feed/posts/${encodeURIComponent(id)}/comments`, {
      method: "POST",
      body: JSON.stringify({ body, ...(parentId ? { parentId } : {}) }),
    }),
  feedCommentDelete: (id: string) =>
    userHttp<{ ok: true }>(`/feed/comments/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /** GOON ROOMS — the host's doors. No track() here: the pages know public/role. */
  roomCreate: (body: RoomCreateBody) =>
    userHttp<RoomResponse>("/rooms", { method: "POST", body: JSON.stringify(body) }),
  roomClose: (id: string) => userHttp<{ ok: true }>(`/rooms/${encodeURIComponent(id)}/close`, { method: "POST" }),
  roomSetView: (id: string, viewId: string | null): Promise<{ room: Room }> =>
    userHttp<RoomResponse>(`/rooms/${encodeURIComponent(id)}/view`, { method: "PUT", body: JSON.stringify({ viewId }) }),
  roomBan: (id: string, username: string) =>
    userHttp<{ ok: true }>(`/rooms/${encodeURIComponent(id)}/bans`, { method: "POST", body: JSON.stringify({ username }) }),
  roomUnban: (id: string, username: string) =>
    userHttp<{ ok: true }>(`/rooms/${encodeURIComponent(id)}/bans/${encodeURIComponent(username)}`, { method: "DELETE" }),

  /** The buyer's vault, each item with its standing on the wall (none / pending / live) —
   *  what the buy dialog's Upload tab lists. The wall has no upload of its own since
   *  2026-09-12: a file goes in through `vaultUpload` and is picked from here. */
  wallUploads: () => userHttp<WallUploadsResponse>("/wall/uploads/mine"),

  // ---- the vault (2026-09-09): your own uploads, private by construction ----
  vaultStatus: () => userHttp<VaultStatus>("/vault"),
  /** One step of space for Faps. 409 {needed} when short; 409 {goldUpsell} at Basic's ceiling. */
  vaultExpand: () =>
    userHttp<{ ok: true; vault: VaultStatus }>("/vault/expand", { method: "POST" }),
  /**
   * Send one file to the vault.
   *
   * FormData, NOT userHttp's JSON path: a multipart body carries its own generated
   * boundary in the content-type, and userHttp always writes `application/json`, which
   * would make the server read the parts as a JSON document and fail. Setting no
   * content-type at all is what lets the browser fill in the boundary. The attestation
   * rides beside the bytes: the server refuses the upload whole rather than storing
   * something it then has to walk back.
   */
  vaultUpload: async (file: File): Promise<{ item: MediaItem; vault: VaultStatus; existing?: true }> => {
    const body = new FormData();
    body.append("attested", "true");
    body.append("file", file);
    const token = getUserToken();
    const res = await fetch(`${BASE}/uploads`, {
      method: "POST",
      headers: token ? { authorization: `Bearer ${token}` } : {},
      body,
    });
    if (!res.ok) {
      let message = `${res.status} ${res.statusText}`;
      let parsed: ApiError | undefined;
      try {
        parsed = (await res.json()) as ApiError;
        if (parsed?.error) message = `${res.status}: ${parsed.error}`;
      } catch {
        /* a refusal with no body still has its status */
      }
      // The typed flags (vaultFull, goldUpsell) must survive: the panel picks its door
      // off them, the way the collections cap's refusal survives userHttp.
      const err = new ApiHttpError(res.status, message);
      err.apiError = parsed;
      throw err;
    }
    return (await res.json()) as { item: MediaItem; vault: VaultStatus; existing?: true };
  },
  // Deleting an upload is `removeFromPlaylist(VAULT_COLLECTION_ID, clipKey(item))` — the
  // collection page's one Remove/Delete path. (A separate `vaultRemove` against
  // DELETE /uploads/:clipId existed here with no caller; gone 2026-09-12.)

  /** One object, not nine positionals: registration carries seven fields now, most of
   *  them strings, which is exactly the shape that produces a silent argument-order
   *  bug. `login` keeps its positionals — it genuinely has two and a flag. */
  register: (body: RegisterRequest) =>
    userHttp<AuthResponse>("/auth/register", {
      method: "POST",
      body: JSON.stringify(body),
    }).then((r) => {
      setUserToken(r.token);
      // No track() here: account_created is sent once, by the session store
      // (lib/session.ts), after identify. Sending it here too counted every sign-up twice.
      return r;
    }),

  login: (username: string, password: string, gooncha?: string) =>
    userHttp<AuthResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password, ...(gooncha ? { gooncha } : {}) }),
    }).then((r) => {
      setUserToken(r.token);
      // No track() here either: signed_in is the session store's, once per sign-in.
      return r;
    }),

  logout: () => userHttp<{ ok: true }>("/auth/logout", { method: "POST" }).finally(clearUserToken),

  /** Password recovery, step one: name the account (behind the robot check) and get its
   *  three question ids plus the ticket step two carries. */
  recoverStart: (body: RecoverStartRequest) =>
    userHttp<RecoverStartResponse>("/auth/recover/start", { method: "POST", body: JSON.stringify(body) }),
  /** Step two: all three answers and the new password. Signs in on success, like login. */
  recoverFinish: (body: RecoverFinishRequest) =>
    userHttp<AuthResponse>("/auth/recover/finish", { method: "POST", body: JSON.stringify(body) }).then((r) => {
      setUserToken(r.token);
      return r;
    }),
  /** Signed in: change the password. Every other session is signed out. */
  changePassword: (body: ChangePasswordRequest) =>
    userHttp<{ ok: true }>("/auth/password", { method: "POST", body: JSON.stringify(body) }),
  /** Signed in: erase the account. The token dies with it, so it is dropped here too. */
  deleteAccount: (body: DeleteAccountRequest) =>
    userHttp<{ ok: true }>("/auth/delete-account", { method: "POST", body: JSON.stringify(body) }).then((r) => {
      clearUserToken();
      return r;
    }),

  me: () => userHttp<{ user: User }>("/auth/me"),

  /**
   * The profile picture (2026-09-13): one image file, cut to a square server-side. FormData
   * for vaultUpload's reason (a multipart body carries its own boundary). Comes back as
   * the refreshed User, avatarUrl on it, so the session can take it without a second read.
   */
  avatarUpload: async (file: File): Promise<{ user: User }> => {
    const body = new FormData();
    body.append("attested", "true");
    body.append("file", file);
    const token = getUserToken();
    const res = await fetch(`${BASE}/auth/avatar`, { method: "POST", headers: token ? { authorization: `Bearer ${token}` } : {}, body });
    if (!res.ok) {
      let message = `${res.status} ${res.statusText}`;
      try {
        const parsed = (await res.json()) as ApiError;
        if (parsed?.error) message = `${res.status}: ${parsed.error}`;
      } catch {
        /* a refusal with no body still has its status */
      }
      throw new ApiHttpError(res.status, message);
    }
    return (await res.json()) as { user: User };
  },
  avatarRemove: () => userHttp<{ user: User }>("/auth/avatar", { method: "DELETE" }),

  /** Flip whether /u/<username> serves your stats to anyone who opens it. */
  setProfilePublic: (profilePublic: boolean) =>
    userHttp<{ user: User }>("/auth/me", {
      method: "PATCH",
      body: JSON.stringify({ profilePublic }),
    }),

  /** The account's own four facts, for /settings. Its own door rather than /auth/me —
   *  see AccountProfile in shared: a User goes to strangers and this never does. */
  accountProfile: () => userHttp<AccountProfileResponse>("/auth/profile"),

  /** Fill in or change them. Send only what changed; a body with none of the four is
   *  a 400 rather than a silent success. `security` replaces all three at once. */
  updateAccountProfile: (patch: AccountProfilePatch) =>
    userHttp<AccountProfileResponse>("/auth/profile", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  classify: (clip: MediaItem, verdict: ClipVerdict, categories: string[]) =>
    userHttp<ClassificationResponse>("/clips/classify", {
      method: "POST",
      body: JSON.stringify({ clip, verdict, categories }),
    }),

  getClassification: (clipId: string) =>
    userHttp<ClassificationResponse>(`/clips/classification?clipId=${encodeURIComponent(clipId)}`),

  listCatalog: (opts: { verdict?: ClipVerdict; category?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.verdict) q.set("verdict", opts.verdict);
    if (opts.category) q.set("category", opts.category);
    const qs = q.toString();
    return userHttp<CatalogResponse>(`/clips${qs ? `?${qs}` : ""}`);
  },

  listCategories: () => userHttp<{ categories: string[] }>("/clips/categories"),

  // playlists
  listPlaylists: () => userHttp<PlaylistsResponse>("/playlists"),

  // `surface` is required so a new creation path has to say where it lives — the
  // wrapper cannot tell the hub form from the add-menu inline create from a clone.
  createPlaylist: (name: string, surface: "hub" | "add_menu" | "clone") =>
    userHttp<Playlist>("/playlists", { method: "POST", body: JSON.stringify({ name }) }).then(
      (r) => {
        track("collection_created", { surface });
        return r;
      },
    ),

  getPlaylist: (id: string) => userHttp<PlaylistDetail>(`/playlists/${encodeURIComponent(id)}`),

  renamePlaylist: (id: string, name: string) =>
    userHttp<Playlist>(`/playlists/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }).then((r) => {
      // The act only — the new name is the user's own words and never travels.
      track("collection_renamed");
      return r;
    }),

  /** Patch a playlist's name/description/public flag. `opts.itemCount` is how many
   *  clips the collection held at the moment of the act — publishing an empty shelf
   *  and publishing a full one are different signals on the dashboard. */
  updatePlaylist: (id: string, patch: UpdatePlaylistRequest, opts?: { itemCount?: number }) =>
    userHttp<Playlist>(`/playlists/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }).then((r) => {
      // Independent ifs, one event per field present: the UI patches one field at a
      // time today, and a future multi-field patch should count each edit. A `name`
      // here is renamePlaylist's business, not an edit. Clearing the thumbnail
      // (null) is still a thumbnail edit.
      if (typeof patch.public === "boolean")
        track("collection_published", { public: patch.public, item_count: opts?.itemCount ?? null });
      if (patch.description !== undefined) track("collection_edited", { field: "description" });
      if (patch.thumbnailClipId !== undefined) track("collection_edited", { field: "thumbnail" });
      return r;
    }),

  deletePlaylist: (id: string, opts?: { itemCount?: number }) =>
    userHttp<{ ok: true }>(`/playlists/${encodeURIComponent(id)}`, { method: "DELETE" }).then(
      (r) => {
        track("collection_deleted", { item_count: opts?.itemCount ?? null });
        return r;
      },
    ),

  addToPlaylist: (id: string, clip: MediaItem, opts?: { silent?: boolean }) =>
    userHttp<{ ok: true }>(`/playlists/${encodeURIComponent(id)}/items`, {
      method: "POST",
      body: JSON.stringify({ clip }),
    }).then((r) => {
      // The act, not the clip and not the collection: see the header of analytics.ts.
      // `silent` is for the bulk clone, which is ONE act — it rolls its adds up into
      // a single collection_cloned instead of one event per clip.
      if (!opts?.silent) track("added_to_collection", { target: "collection", via: "single" });
      return r;
    }),

  removeFromPlaylist: (id: string, clipId: string) => {
    // "faps" is the reserved virtual-collection id (see Collections.tsx / the server).
    const target = id === "faps" ? "faps" : "collection";
    return userHttp<{ ok: true }>(
      `/playlists/${encodeURIComponent(id)}/items/${encodeURIComponent(clipId)}`,
      { method: "DELETE" },
    ).then((r) => {
      // Counts curating a collection page — including Faps. An un-fap via toggleFap
      // stays untracked (see its comment); these are different acts.
      track("removed_from_collection", { target, via: "single" });
      return r;
    });
  },

  /** Bulk select (issue #12): move / add / remove many clips in ONE request. Untracked
   *  here on purpose — the page fires one collection_bulk_action with the count, the way
   *  the clone rolls its adds into one collection_cloned (see analytics.ts). */
  bulkCollection: (id: string, req: BulkCollectionRequest) =>
    userHttp<BulkCollectionResponse>(`/playlists/${encodeURIComponent(id)}/bulk`, {
      method: "POST",
      body: JSON.stringify(req),
    }),

  undoBulkCollection: (token: string) =>
    userHttp<{ ok: true }>("/playlists/bulk/undo", { method: "POST", body: JSON.stringify({ token }) }),

  // faps (likes)
  fapStatus: (clip: MediaItem) =>
    userHttp<FapStatusResponse>(`/faps?clip=${encodeURIComponent(clipKey(clip))}`),
  toggleFap: (clip: MediaItem) =>
    userHttp<FapStatusResponse>("/faps", { method: "POST", body: JSON.stringify({ clip }) }).then(
      (r) => {
        // Faps IS a collection — the default one — so adding to it is the same act as
        // adding to a playlist, told apart by `target`. Removing is not tracked: the
        // question is whether people collect at all, and un-collecting answers it too.
        if (r.fapped) track("added_to_collection", { target: "faps" });
        return r;
      },
    ),
  /** Spammable "like": add-to-library (idempotent) + bump the click tally. */
  // NOT tracked here. `fap_saved` fires at the five UI call sites instead, because it
  // carries the SURFACE the fap happened on (screen, browse, fapbar, clip, collection) and
  // this layer cannot tell them apart. An event here as well would count every fap twice.
  /** One press of the loop button on a screen — pins this clip to a region.
   *  Fire-and-forget; a signed-out press 401s and is simply not counted. */
  recordLoop: (clip: MediaItem) =>
    userHttp<{ ok: true }>("/loops", { method: "POST", body: JSON.stringify({ clip }) }),

  fapClick: (clip: MediaItem) =>
    userHttp<FapClickResponse>("/faps/click", { method: "POST", body: JSON.stringify({ clip }) }),

  // FAPS (the currency). No tracking in this layer for the same reason as fapClick:
  // the dialog knows which door was taken and this layer does not.
  fapsWallet: () => userHttp<FapsWalletResponse>("/faps/wallet"),
  /** Deal today's chest — behind sign-in and a day, since the GOONCHA pass was dropped
   *  2026-09-09. */
  fapsChestDeal: () =>
    userHttp<FapsChestDeal>("/faps/chest/deal", { method: "POST", body: "{}" }),
  fapsChestPick: (dealId: string, index: number) =>
    userHttp<FapsChestResponse>("/faps/chest/pick", { method: "POST", body: JSON.stringify({ dealId, index }) }),
  fapsRedeem: (code: string) =>
    userHttp<FapsRedeemResponse>("/faps/redeem", { method: "POST", body: JSON.stringify({ code }) }),
  /** A client-verified step of a daily quest — only the handful of quests with no other
   *  server signal (Infinite Scroll's page-advance, "watch this surface for N minutes")
   *  go through this door; every other quest bumps itself at the route that already does
   *  the thing. `viewId` is only read for "watch_view" — the server checks it against
   *  today's `pickWatchTarget` pick itself, so a stale or wrong id here just no-ops. */
  fapsQuestBump: (questId: QuestId, viewId?: string) =>
    userHttp<{ ok: true }>("/faps/quest/bump", {
      method: "POST",
      body: JSON.stringify(viewId ? { questId, viewId } : { questId }),
    }),

  // per-clip tags (global per clip; any signed-in user may edit)
  /** Read a clip's current global (approved) tags. */
  getClipTags: (clip: MediaItem) =>
    userHttp<ClipTagsResponse>(`/clips/tags?clip=${encodeURIComponent(clipKey(clip))}`),
  /** Aggregate stats for one clip. Public, but the token (if any) surfaces the
   *  caller's own collections alongside public / Editor's Choice ones. */
  getClipDetails: (clipKey: string) =>
    userHttp<ClipDetailsResponse>(`/clips/details?clip=${encodeURIComponent(clipKey)}`),
  /** Suggest a single tag for a clip (pending admin review — not applied directly). */
  suggestTag: (clip: MediaItem, tag: string) =>
    userHttp<{ ok: true }>("/clips/tags/suggest", {
      method: "POST",
      body: JSON.stringify({ clip, tag } satisfies SuggestTagRequest),
    }).then((r) => {
      // Neither the clip nor the tag: a tag is a word about a clip, and together they are
      // a description of what somebody was watching.
      track("tag_suggested");
      return r;
    }),
  /** Flag an AI-assigned keyword as mislabelled (queues it for admin review).
   *  Rejects with 409 if the keyword isn't AI-sourced. */
  reportTag: (clip: MediaItem, tag: string, note?: string) =>
    userHttp<{ ok: true }>("/clips/tags/report", {
      method: "POST",
      body: JSON.stringify({ clip, tag, note } satisfies ReportTagRequest),
    }).then((r) => {
      track("tag_reported");
      return r;
    }),
  // stats
  /** `endedAt`, ms since epoch: when the interval closed. The server clamps it into the
   *  last hour and defaults it to arrival, so an old client keeps working; a new one
   *  says it so wall-clock time can lay intervals end to end (lib/watchReport.ts). */
  recordWatch: (clip: MediaItem, seconds: number, endedAt?: number) =>
    userHttp<{ ok: true }>("/stats/watch", {
      method: "POST",
      body: JSON.stringify({ clip, seconds, endedAt }),
    }),
  /** A screen's batched watch intervals — see lib/watchReport.ts for who buffers them.
   *  `keepalive` so the last window survives the tab closing, clipViews' precedent. */
  recordWatchBatch: (
    events: Array<{ clip: MediaItem; seconds: number; endedAt?: number; wall?: boolean }>,
    keepalive = false,
  ) =>
    userHttp<{ ok: true; wallSeconds?: number }>("/stats/watch-batch", {
      method: "POST",
      body: JSON.stringify({ events }),
      keepalive,
    }),
  /** The daily wall allowance as the ledger has it — see lib/wallAllowance.ts. */
  wallAllowance: () =>
    userHttp<{ wallSeconds: number; wallMinutesPerDay: number | null; dayStart: number }>("/stats/allowance"),

  getStats: () => userHttp<GoonStats>("/stats"),
};
